import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Spin, Card, DatePicker, Row, Col, Space, Table, Alert, Divider } from 'antd';
import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, Cell, Brush } from 'recharts';
import { DashboardOutlined, CalendarOutlined, CheckCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';

const { Title, Text, Paragraph } = Typography;

// Color System
const COLORS = {
  primary: '#0052CC',       // Apraava Blue
  secondary: '#E67E22',     // Apraava Orange
  safe: '#27AE60',          // GDAM Green
  danger: '#E74C3C',        // Red
  neutral: '#64748b',       // Grey
  light: '#f8fafc',
  border: '#e2e8f0',
};

export default function DsmMinDashboardPage() {
  const [selectedDate, setSelectedDate] = useState(dayjs().subtract(1, 'day')); // Defaults to previous day
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const dateStr = selectedDate.format('YYYY-MM-DD');

  useEffect(() => {
    fetchDashboardData();
  }, [dateStr]);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/dsm/${dateStr}/`);
      setData(res.data);
    } catch (err) {
      console.error(err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const tableColumns = [
    { title: 'Block', dataIndex: 'time_block', key: 'time_block', width: '12%', align: 'center' },
    { title: 'Forecast (MW)', dataIndex: 'forecast_schedule', key: 'forecast_schedule', align: 'right', render: v => v.toFixed(2) },
    { title: 'AVC (MW)', dataIndex: 'avc', key: 'avc', align: 'right', render: v => v.toFixed(2) },
    { title: 'Actual Gen (MW)', dataIndex: 'actual_generation', key: 'actual_generation', align: 'right', render: v => v.toFixed(2) },
    {
      title: 'Deviation (MW)',
      dataIndex: 'deviation_mw',
      key: 'deviation_mw',
      align: 'right',
      render: v => <span style={{ color: v >= 0 ? COLORS.safe : COLORS.danger, fontWeight: 600 }}>{v >= 0 ? `+${v.toFixed(2)}` : v.toFixed(2)}</span>
    },
    {
      title: 'Deviation %',
      dataIndex: 'deviation_pct',
      key: 'deviation_pct',
      align: 'right',
      render: v => <span style={{ fontWeight: 600 }}>{v.toFixed(2)}%</span>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: s => <span style={{ padding: '2px 8px', borderRadius: 4, background: s === 'OI' ? '#e8f5e9' : '#ffebee', color: s === 'OI' ? COLORS.safe : COLORS.danger, fontWeight: 700, fontSize: 11 }}>{s}</span>
    },
    { title: 'Penalty (Rs)', dataIndex: 'dsm_penalty', key: 'dsm_penalty', align: 'right', render: v => v > 0 ? <span style={{ color: COLORS.danger }}>₹{v.toLocaleString()}</span> : '—' },
    { title: 'Revenue Pre-DSM (Rs)', dataIndex: 'revenue_pre_dsm', key: 'revenue_pre_dsm', align: 'right', render: v => `₹${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}` },
    { title: 'Revenue Post-DSM (Rs)', dataIndex: 'revenue_post_dsm', key: 'revenue_post_dsm', align: 'right', render: v => <strong style={{ color: COLORS.primary }}>₹{v.toLocaleString(undefined, { maximumFractionDigits: 0 })}</strong> },
  ];


  const energySegData = useMemo(() => {
    if (!data?.kpis) return [];
    const k = data.kpis;
    const val_gt_20 = k.seg_gt_20 / 1000.0;
    const val_15_20 = k.seg_15_20 / 1000.0;
    const val_12_15 = k.seg_12_15 / 1000.0;
    const val_safe = k.seg_safe / 1000.0;
    const val_neg_12_15 = k.seg_neg_12_15 / 1000.0;
    const val_neg_15_20 = k.seg_neg_15_20 / 1000.0;
    const val_lt_neg_20 = k.seg_lt_neg_20 / 1000.0;

    const totalSchedMwh = (k.total_schedule_mu || 0) * 1000.0;
    const getPct = (val) => totalSchedMwh > 0 ? (val / totalSchedMwh * 100) : 0;

    return [
      { name: '> 20%', value: val_gt_20, pct: getPct(val_gt_20), color: '#E74C3C' },
      { name: '15% to 20%', value: val_15_20, pct: getPct(val_15_20), color: '#E74C3C' },
      { name: '12% to 15%', value: val_12_15, pct: getPct(val_12_15), color: '#E74C3C' },
      { name: '-12% to 12%', value: val_safe, pct: getPct(val_safe), color: '#27AE60' },
      { name: '-15% to -12%', value: val_neg_12_15, pct: getPct(val_neg_12_15), color: '#E74C3C' },
      { name: '-20% to -15%', value: val_neg_15_20, pct: getPct(val_neg_15_20), color: '#E74C3C' },
      { name: '< -20%', value: val_lt_neg_20, pct: getPct(val_lt_neg_20), color: '#E74C3C' },
    ];
  }, [data]);

  const renderCustomBarLabel = (props) => {
    const { x, y, width, height, index } = props;
    const item = energySegData[index];
    if (!item) return null;

    const text = `${item.value.toFixed(2)} (${item.pct.toFixed(2)}%)`;
    const isInside = width > 130;
    const textX = isInside ? x + width - 10 : x + width + 10;
    const textAnchor = isInside ? 'end' : 'start';
    const textColor = isInside ? '#ffffff' : '#333333';

    return (
      <text
        x={textX}
        y={y + height / 2}
        fill={textColor}
        textAnchor={textAnchor}
        dominantBaseline="middle"
        style={{ fontSize: 10, fontWeight: 700, fontFamily: 'Inter, sans-serif' }}
      >
        {text}
      </text>
    );
  };

  return (
    <div style={{ padding: '32px 40px', minHeight: '100vh', background: '#f8fafc' }}>
      {/* Top Header Section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(0, 82, 204, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DashboardOutlined style={{ color: COLORS.primary, fontSize: 22 }} />
          </div>
          <div>
            <Title level={2} style={{ margin: 0, fontWeight: 700, color: '#0f172a' }}>
              DSM Minimization Dashboard
            </Title>
            <Paragraph style={{ color: '#64748b', margin: 0, fontSize: 13 }}>
              Continuous time-block tracking and penalty minimization analytics.
            </Paragraph>
          </div>
        </div>

        {/* Date Selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <CalendarOutlined style={{ color: COLORS.neutral }} />
          <DatePicker
            value={selectedDate}
            onChange={(date) => date && setSelectedDate(date)}
            allowClear={false}
            style={{ borderRadius: 8, height: 38 }}
          />
        </div>
      </div>

      {error && (
        <Alert message={error} type="error" showIcon style={{ marginBottom: 24, borderRadius: 12 }} />
      )}

      {loading ? (
        <div style={{ padding: '100px 0', textAlign: 'center' }}>
          <Spin size="large" tip="Calculating DSM block statistics..." />
        </div>
      ) : data?.blocks.length === 0 ? (
        <Alert
          message="No Ingested Data Available"
          description="There is no raw wind energy data uploaded for this date yet. Please go to the Excel Ingestion tab to upload files first."
          type="info"
          showIcon
          style={{ borderRadius: 12 }}
        />
      ) : data ? (
        <Space direction="vertical" size={24} style={{ width: '100%' }}>

          {/* KPI Dashboard Row */}
          <Row gutter={[20, 20]}>
            <Col xs={12} sm={8} lg={4}>
              <Card bordered={false} style={{ borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }} styles={{ body: { padding: '16px 20px' } }}>
                <Text style={{ color: '#64748b', fontSize: 12 }}>Installed Capacity</Text>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginTop: 4 }}>252 MW <span style={{ fontSize: 12, fontWeight: 500 }}>MW</span></div>
              </Card>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Card bordered={false} style={{ borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }} styles={{ body: { padding: '16px 20px' } }}>
                <Text style={{ color: '#64748b', fontSize: 12 }}>Scheduled Gen</Text>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginTop: 4 }}>{data.kpis.total_schedule_mu.toFixed(2)} <span style={{ fontSize: 12, fontWeight: 500 }}>MUs</span></div>
              </Card>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Card bordered={false} style={{ borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }} styles={{ body: { padding: '16px 20px' } }}>
                <Text style={{ color: '#64748b', fontSize: 12 }}>Actual Gen</Text>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginTop: 4 }}>{data.kpis.total_generation_mu.toFixed(2)} <span style={{ fontSize: 12, fontWeight: 500 }}>MUs</span></div>
              </Card>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Card bordered={false} style={{ borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }} styles={{ body: { padding: '16px 20px' } }}>
                <Text style={{ color: '#64748b', fontSize: 12 }}>PLF</Text>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginTop: 4 }}>{data.kpis.plf.toFixed(2)}%</div>
              </Card>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Card bordered={false} style={{ borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }} styles={{ body: { padding: '16px 20px' } }}>
                <Text style={{ color: '#64748b', fontSize: 12 }}>Net DSM Impact</Text>
                <div style={{ fontSize: 20, fontWeight: 700, color: data.kpis.net_dsm_impact >= 0 ? COLORS.safe : COLORS.danger, marginTop: 4 }}>
                  {data.kpis.net_dsm_impact >= 0 ? `+₹${data.kpis.net_dsm_impact.toFixed(2)}` : `-₹${Math.abs(data.kpis.net_dsm_impact).toFixed(2)}`}
                  <span style={{ fontSize: 12, fontWeight: 500, color: COLORS.neutral }}> /kWh</span>
                </div>
              </Card>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Card bordered={false} style={{ borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.02)', borderLeft: `4px solid ${COLORS.danger}` }} styles={{ body: { padding: '16px 20px' } }}>
                <Text style={{ color: '#64748b', fontSize: 12 }}>DSM Penalty</Text>
                <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.danger, marginTop: 4 }}>₹{data.kpis.total_penalty.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
              </Card>
            </Col>
          </Row>

          {/* Visualizations Row */}
          <Row gutter={[24, 24]}>
            {/* Power Forecast vs Actual */}
            <Col xs={24} lg={16}>
              <Card title={<span style={{ fontWeight: 600, fontSize: 15 }}>Power Forecast vs Actual Generation</span>} bordered={false} style={{ borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                <div style={{ height: 350, width: '100%' }}>
                  <ResponsiveContainer>
                    <ComposedChart data={data.blocks} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="time_block" stroke="#94a3b8" fontSize={10} tickLine={false} interval={3} tickFormatter={(v) => v ? v.split(' ')[0] : ''} />
                      <YAxis label={{ value: 'MW', angle: -90, position: 'insideLeft', offset: 10 }} stroke="#94a3b8" fontSize={11} tickLine={false} />
                      <Tooltip labelFormatter={(label) => `Time: ${label}`} formatter={(value, name) => [Number(value).toFixed(2) + ' MW', name]} contentStyle={{ fontSize: 11 }} />
                      <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                      <Line name="Actual Generation (MW)" type="monotone" dataKey="actual_generation" stroke={COLORS.primary} strokeWidth={2.5} dot={false} />
                      <Line name="Forecasted Schedule (MW)" type="monotone" dataKey="forecast_schedule" stroke={COLORS.secondary} strokeWidth={2} strokeDasharray="5 5" dot={false} />
                      <Brush dataKey="time_block" height={20} stroke="#cbd5e1" tickFormatter={() => ''} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </Col>

            {/* Energy Deviation Segregation */}
            <Col xs={24} lg={8}>
              <Card title={<span style={{ fontWeight: 600, fontSize: 15 }}>Energy Deviation Segregation</span>} bordered={false} style={{ borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                <div style={{ height: 350, width: '100%' }}>
                  <ResponsiveContainer>
                    <BarChart data={energySegData} layout="vertical" margin={{ top: 5, right: 80, left: 10, bottom: 25 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                      <XAxis type="number" stroke="#94a3b8" fontSize={11} label={{ value: 'Energy in MWh', position: 'insideBottom', offset: -5, style: { fontSize: 11 } }} />
                      <YAxis type="category" dataKey="name" tick={{ fill: '#4b5563', fontWeight: 600, fontSize: 11 }} width={90} />
                      <Tooltip formatter={(value, name, entry) => [`${Number(value).toFixed(2)} MWh (${entry.payload.pct.toFixed(2)}%)`, 'Energy']} />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={20} label={renderCustomBarLabel}>
                        {energySegData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Details Table Card */}
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <span style={{ fontWeight: 600, fontSize: 15 }}>15-Minute Time-Block Data Calculations</span>
                <span style={{ fontSize: 12, fontWeight: 500, color: COLORS.neutral }}>96 Blocks Total</span>
              </div>
            }
            bordered={false}
            style={{ borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}
            styles={{ body: { padding: 0 } }}
          >
            <Table
              dataSource={data.blocks}
              columns={tableColumns}
              rowKey="time_block"
              pagination={{ pageSize: 24, showSizeChanger: false, position: ['bottomCenter'] }}
              style={{ borderRadius: '0 0 16px 16px', overflow: 'hidden' }}
            />
          </Card>
        </Space>
      ) : null}
    </div>
  );
}
